import { ClassNames } from '@emotion/react'
import React, {
  type ComponentProps,
  createContext,
  type ReactNode,
  useContext,
  useMemo,
} from 'react'

import {
  ExtendedPaletteColorName,
  getDarkThemeDataVisualizationColor,
  getDarkThemeExtendedColor,
  getIsDataVisualizationPaletteColorName,
  getIsExtendedPaletteColorName,
  getLightThemeDataVisualizationColor,
  getLightThemeExtendedColor,
  getNeutralColor,
  NeutralPaletteColors,
} from '../../shared/MobiusColors'
import { dedent } from '../../util/dedent'
import { typeSafeObjectEntries } from '../../util/typeSafeObjectEntries'
import type {
  NeutralColorToken,
  OverlayColorToken,
  SemanticColorToken,
} from '../themes'
import {
  type ColorToken,
  CssVarNames,
  CssVars,
  getIsSemanticColorToken,
  lightTheme,
  type NeutralPalette,
  type ProductTheme,
  type SemanticPalette,
  type ThemeColorToken,
} from '../themes'
import { getInitialSizeCssVars } from '../themes/sizePalette'
import {
  getInitialTypographyCssVars,
  getResponsiveTypographyCssVars,
} from '../themes/typePalette'
/**
 * The primary way we specify the theme is via CSS variables, since theming should operate on
 * an entire DOM subtree. But we also store it in React Context so that we can easily use it
 * across Portals and for the rare case where we need the value in JS (e.g. specifying colors
 * for third-party APIs like Google Maps or Lottie).
 */
const ThemeContext = createContext<{
  theme: ProductTheme
  elevation: number
}>({
  elevation: 0,
  theme: lightTheme,
})

/**
 * React context to store the custom styles provided to ThemelessBentoProvider's
 * __dangerouslyInjectStylesIntoThemeProvider prop, to be injected into every instance
 * of ThemeProvider.
 */
export const InjectStylesIntoThemeProviderContext = createContext<{
  [cssProperty: string]: string
}>({})

/**
 * Renders all children (in both the React and DOM trees) with a given theme.
 */
export function ThemeProvider({
  children,
  theme,
}: {
  /**
   * A standard Robinhood theme object, as defined here in Bento Web.
   */
  theme: ProductTheme
  /**
   * The children to render, as a render prop.
   */
  children: (_: {
    /**
     * A CSS class name to attach to the immediate child element. This class will apply
     * the new theme to the DOM subtree of that child.
     */
    rootClassName: string
    /**
     * A string containing CSS code to inject into the immediate child element. This CSS
     * applies the new theme to the DOM subtree of that child. This is primarily useful for
     * injecting global theming via Emotion's `Global` component.
     */
    __experimentalCssString: string
  }) => ReactNode
}) {
  const elevation = useThemeElevation()
  return (
    <_InternalThemeProvider elevation={elevation} theme={theme}>
      {children}
    </_InternalThemeProvider>
  )
}

/**
 * Renders all children components with a given `theme`, `elevation`, and CSS variables.
 *
 * Note that the behavior will vary across Portals depending on what props you provide. Any
 * provided `theme` or `elevation` will affect all children in the React and DOM trees, but
 * any provided `variables` will only affect children in the DOM tree. If you provide both
 * `theme` and `variables`, `variables` takes precedence for all children in the DOM tree,
 * but the `theme` will have an effect across any Portals.
 *
 * Should only be used directly by theme helpers within Bento Web. All components and external
 * users should use `ThemeProvider`.
 */
export function _InternalThemeProvider({
  children,
  elevation: elevationProp,
  theme: themeProp,
  variables: variablesProp,
}: {
  theme?: ProductTheme
  elevation?: number
  variables?:
    | ComponentProps<typeof _InternalDeclareCssVariables>['variables']
    | null
  children: (p: {
    rootClassName: string
    __experimentalCssString: string
  }) => ReactNode
}) {
  const parentSpec = useTheme()
  const parentElevation = useThemeElevation()

  // Define the new theme, elevation, etc. based on the provided props, in each case using
  // the current theme context as a fallback.
  const theme = themeProp || parentSpec
  const elevation =
    typeof elevationProp === 'number' ? elevationProp : parentElevation
  const variables = useMemo(
    () =>
      variablesProp || {
        ...getInitialSemanticColorVars(theme, elevation),
        ...getInitialNeutralColorCssVars(theme, elevation),
        ...getInitialTextColorCssVars(theme, elevation),
        ...getInitialShadowColorCssVars(theme),
        ...getInitialFocusColorCssVars(theme, elevation),
        ...getInitialTypographyCssVars(),
        ...getInitialSizeCssVars(),
      },
    [elevation, theme, variablesProp]
  )
  const responsiveVariables = useMemo(
    () =>
      typeSafeObjectEntries(getResponsiveTypographyCssVars())
        .map(
          ([mediaQuery, scopedVariables]) =>
            dedent`${mediaQuery} {${generateStylesFromVariablesDeclarations(
              scopedVariables
            )}}`
        )
        .join('\n'),
    []
  )

  const additionalCustomStyles: string = useMemo(
    () =>
      Object.entries({
        'color-scheme': theme.isDark ? 'dark' : 'light',
      })
        .map(([key, value]) => `${key}: ${value};`)
        .join('\n'),
    [theme]
  )

  const newContextValue = useMemo(
    () => ({ elevation, theme }),
    [elevation, theme]
  )
  return (
    <ThemeContext.Provider value={newContextValue}>
      <_InternalDeclareCssVariables
        additionalCustomStyles={additionalCustomStyles}
        additionalGeneratedStyles={responsiveVariables}
        variables={variables}
      >
        {children}
      </_InternalDeclareCssVariables>
    </ThemeContext.Provider>
  )
}

export type CssVariableDeclarations = {
  [cssVariableName: string]: string | number
}
export type ResponsiveCssVariableDeclarations = {
  [mediaQuery: string]: CssVariableDeclarations
}

/**
 * Generates a CSS class name that declares the passed in CSS variables at the DOM node the class is applied to.
 *
 * Only for use within Bento Web; do not use this in app code.
 */
export function _InternalDeclareCssVariables({
  additionalCustomStyles,
  additionalGeneratedStyles,
  children,
  variables,
}: {
  children: (p: {
    rootClassName: string
    __experimentalCssString: string
  }) => ReactNode
  variables: CssVariableDeclarations
  additionalCustomStyles?: string
  additionalGeneratedStyles?: string
}) {
  const stylesToInject = useContext(InjectStylesIntoThemeProviderContext)

  let cssString = generateStylesFromVariablesDeclarations(
    variables,
    stylesToInject
  )

  if (additionalGeneratedStyles) {
    cssString += additionalGeneratedStyles
  }

  if (additionalCustomStyles) {
    cssString += additionalCustomStyles
  }

  return (
    <ClassNames>
      {({ css: getClassName }) =>
        children({
          __experimentalCssString: cssString,
          rootClassName: cssString ? getClassName(cssString) : '',
        })
      }
    </ClassNames>
  )
}

function generateStylesFromVariablesDeclarations(
  ...variableDeclarations: Array<CssVariableDeclarations>
) {
  return variableDeclarations
    .reduce((accumulator, variables) => {
      return [
        ...accumulator,
        ...Object.entries(variables).map(([k, v]) => `${k}: ${v};`),
      ]
    }, [] as Array<string>)
    .join('\n')
}

/**
 * Returns an object of the CSS variables for our neutral colors, given the provided `theme`
 * and `elevation`.
 */
export function getInitialNeutralColorCssVars(
  theme: ProductTheme,
  elevation: number
) {
  const neutral = theme.getNeutralColorPalette(elevation)
  return {
    [CssVarNames.neutral.fg1]: NeutralPaletteColors[neutral.fg1].base,
    [CssVarNames.neutral.fg2]: NeutralPaletteColors[neutral.fg2].base,
    [CssVarNames.neutral.fg3]: NeutralPaletteColors[neutral.fg3].base,
    [CssVarNames.neutral.bg3]: NeutralPaletteColors[neutral.bg3].base,
    [CssVarNames.neutral.bg2]: NeutralPaletteColors[neutral.bg2].base,
    [CssVarNames.neutral.bg1]: NeutralPaletteColors[neutral.bg1].base,

    [CssVarNames.neutral.hoverFg1]: NeutralPaletteColors[neutral.fg1].hover,
    [CssVarNames.neutral.hoverFg2]: NeutralPaletteColors[neutral.fg2].hover,
    [CssVarNames.neutral.hoverFg3]: NeutralPaletteColors[neutral.fg3].hover,
    [CssVarNames.neutral.hoverBg1]: NeutralPaletteColors[neutral.bg1].hover,
    [CssVarNames.neutral.hoverBg2]: NeutralPaletteColors[neutral.bg2].hover,
    [CssVarNames.neutral.hoverBg3]: NeutralPaletteColors[neutral.bg3].hover,

    [CssVarNames.neutral.pressedFg1]: NeutralPaletteColors[neutral.fg1].pressed,
    [CssVarNames.neutral.pressedFg2]: NeutralPaletteColors[neutral.fg2].pressed,
    [CssVarNames.neutral.pressedFg3]: NeutralPaletteColors[neutral.fg3].pressed,
    [CssVarNames.neutral.pressedBg1]: NeutralPaletteColors[neutral.bg1].pressed,
    [CssVarNames.neutral.pressedBg2]: NeutralPaletteColors[neutral.bg2].pressed,
    [CssVarNames.neutral.pressedBg3]: NeutralPaletteColors[neutral.bg3].pressed,
  }
}

/**
 * Returns an object of the CSS variables for our semantic colors, given the provided `theme` and `elevation`.
 */
export function getInitialSemanticColorVars(
  theme: ProductTheme,
  elevation: number
) {
  const primary = getColorDefinition(
    theme.semanticColors.primary,
    theme,
    elevation
  )
  const positive = getColorDefinition(
    theme.semanticColors.positive,
    theme,
    elevation
  )
  const negative = getColorDefinition(
    theme.semanticColors.negative,
    theme,
    elevation
  )
  const success = getColorDefinition(
    theme.semanticColors.success,
    theme,
    elevation
  )
  const error = getColorDefinition(theme.semanticColors.error, theme, elevation)
  const scary = getColorDefinition(theme.semanticColors.scary, theme, elevation)

  return {
    [CssVarNames.semantic.primary.base]: primary.base,
    [CssVarNames.semantic.primary.hover]: primary.hover,
    [CssVarNames.semantic.primary.pressed]: primary.pressed,
    [CssVarNames.semantic.primary.light]: primary.light,
    [CssVarNames.semantic.primary.lightest]: primary.lightest,

    [CssVarNames.semantic.positive.base]: positive.base,
    [CssVarNames.semantic.positive.light]: positive.light,

    [CssVarNames.semantic.negative.base]: negative.base,
    [CssVarNames.semantic.negative.light]: negative.light,

    [CssVarNames.semantic.success.base]: success.base,
    [CssVarNames.semantic.success.light]: success.light,

    [CssVarNames.semantic.error.base]: error.base,
    [CssVarNames.semantic.error.light]: error.light,

    [CssVarNames.semantic.scary.base]: scary.base,
    [CssVarNames.semantic.scary.light]: scary.light,

    [CssVarNames.overlay.base]: CssVars.neutral.bg1,
    [CssVarNames.overlay.light]: CssVars.semantic.primary.base,
    [CssVarNames.overlay.lightest]: CssVars.semantic.primary.base,

    // NOTE(jare): The following is a CSS variable that is only included to
    // support legacy Sass mixins in web-app. It is defined here because it
    // should not be used in new code, and should not be made available in
    // JavaScript.
    '--rh-LEGACY__primary-color-hover': primary.hover,
  }
}

/**
 * Returns an object of the CSS variables for text colors, given the provided `theme`
 * and `elevation`.
 */
export function getInitialTextColorCssVars(
  theme: ProductTheme,
  elevation: number
) {
  const neutral = theme.getNeutralColorPalette(elevation)
  return {
    [CssVarNames.textColor]: NeutralPaletteColors[neutral.fg1].base,
  }
}

/**
 * Returns an object of the CSS variables for shadow colors, given the provided `theme`.
 */
function getInitialShadowColorCssVars(theme: ProductTheme) {
  return {
    [CssVarNames.shadow.s1]: theme.shadowColors.s1,
    [CssVarNames.shadow.s2]: theme.shadowColors.s2,
  }
}

/**
 * Returns an object of the CSS variables for element focus indicator colors, given the
 * provided `theme`.
 */
function getInitialFocusColorCssVars(theme: ProductTheme, elevation: number) {
  const focusColorComponents = getColorDefinition(
    ExtendedPaletteColorName.Hydro,
    theme,
    elevation
  ).components
  return {
    [CssVarNames.focus
      .innerColor]: `rgb(${focusColorComponents.r}, ${focusColorComponents.g}, ${focusColorComponents.b})`,
    [CssVarNames.focus.outerColor]: `rgba(${focusColorComponents.r}, ${
      focusColorComponents.g
    }, ${focusColorComponents.b}, ${theme.isDark ? '0.4' : '0.25'})`,
  }
}

/**
 * React hook to get the current theme.
 */
export function useTheme(): ProductTheme {
  return useContext(ThemeContext).theme
}

/**
 * React hook to get the current theme's elevation.
 */
export function useThemeElevation(): number {
  return useContext(ThemeContext).elevation
}

/**
 * Helper to get the current primary colors, as CSS variables.
 */
export function getPrimaryColorVars() {
  return CssVars.semantic.primary
}

/**
 * Helper to get the current neutral colors, as an object of CSS values.
 */
export function getNeutralColorVars() {
  return CssVars.neutral
}

/**
 * Helper to get the current semantic colors, as an object of CSS values.
 */
export function getSemanticColorVars() {
  return CssVars.semantic
}

/**
 * Helper to get the current shadow colors, as an object of CSS values.
 */
export function getShadowColorVars() {
  return CssVars.shadow
}

/**
 * Helper to get the current text colors, as a CSS value.
 */
export function getTextColorVar() {
  return CssVars.textColor
}

/**
 * Resolves the provided color, which may be a semantic color, into a ThemeColorToken. This
 * ensures that the resulting color can be reliably used even in the presence of color
 * override components.
 */
export function useResolveToThemeColorToken(
  color: SemanticColorToken | ThemeColorToken | null | undefined | false
): ThemeColorToken | null {
  const theme = useTheme()

  if (!color) {
    return null
  }

  return resolveToThemeColorToken(color, theme)
}

/**
 * Resolves the provided color, which may be a semantic color, into a ThemeColorToken. This
 * ensures that the resulting color can be reliably used even in the presence of color
 * override components.
 */
export function resolveToThemeColorToken(
  color: null | undefined | false,
  theme: ProductTheme
): null
export function resolveToThemeColorToken(
  color: SemanticColorToken | ThemeColorToken,
  theme: ProductTheme
): ThemeColorToken
export function resolveToThemeColorToken(
  color: SemanticColorToken | ThemeColorToken | null | undefined | false,
  theme: ProductTheme
): ThemeColorToken | null {
  if (!color) {
    return null
  }

  if (!getIsSemanticColorToken(color)) {
    return color
  }

  const field = color.split('.')[1] as keyof SemanticPalette
  return theme.semanticColors[field]
}

/**
 * Helper for the useSemanticColor* methods. Deliberately not exported.
 */
function useSemanticColor(color: SemanticColorToken) {
  const theme = useTheme()
  const colorName = resolveToThemeColorToken(color, theme)
  return useThemeColor(colorName)
}

/**
 * Returns a CSS value for the specified semantic color's base variant.
 */
export function useSemanticColorBase(color: SemanticColorToken): string {
  return useSemanticColor(color).base
}

/**
 * Returns an object containing the RGBA components for the specified
 * semantic color's base variant.
 */
export function useSemanticColorComponents(color: SemanticColorToken) {
  return useSemanticColor(color).components
}

/**
 * Returns the color definition object for the specified neutral or extended palette color
 * in the specified theme.
 *
 * Probably should not be used outside Bento Web.
 */
export function getColorDefinition(
  color: ThemeColorToken,
  theme: ProductTheme,
  elevation: number
) {
  if (getIsExtendedPaletteColorName(color)) {
    return theme.isDark
      ? getDarkThemeExtendedColor(color)
      : getLightThemeExtendedColor(color)
  }

  const field = color.split('.')[1] as keyof NeutralPalette
  const name = theme.getNeutralColorPalette(elevation)[field]
  if (!name) {
    throw new Error(
      `Invalid color token "${color}". Unable to parse neutral name.`
    )
  }

  return getNeutralColor(name)
}

/**
 * Helper for the useThemeColor* methods. Deliberately not exported.
 */
function useThemeColor(name: ThemeColorToken) {
  const theme = useTheme()
  const elevation = useThemeElevation()
  return getColorDefinition(name, theme, elevation)
}

/**
 * Returns an object containing the RGBA components of the specified theme
 * color (either a neutral or extended palette color) in the current theme.
 */
export function useThemeColorComponents(name: ThemeColorToken) {
  return useThemeColor(name).components
}

/**
 * Returns a CSS value for the base variant of the specified theme color (either a neutral or
 * extended palette color) in the current theme.
 */
export function useThemeColorBase(name: ThemeColorToken) {
  return useThemeColor(name).base
}

/**
 * Returns a CSS value for the light variant of the specified theme color (either a neutral or
 * extended palette color) in the current theme.
 */
export function useThemeColorLight(name: ThemeColorToken) {
  return useThemeColor(name).light
}

/**
 * Returns a CSS value for the lightest variant of the specified theme color (either a neutral
 * or extended palette color) in the current
 * theme.
 */
export function useThemeColorLightest(name: ThemeColorToken) {
  return useThemeColor(name).lightest
}

/**
 * Returns a CSS value for the specified color in the context of the specified theme.
 */
export function getColorValue(color: ColorToken, theme: ProductTheme): string {
  let value: string
  if (getIsExtendedPaletteColorName(color)) {
    const colorObject = theme.isDark
      ? getDarkThemeExtendedColor(color)
      : getLightThemeExtendedColor(color)
    value = colorObject.base
  } else if (getIsDataVisualizationPaletteColorName(color)) {
    const colorObject = theme.isDark
      ? getDarkThemeDataVisualizationColor(color)
      : getLightThemeDataVisualizationColor(color)
    value = colorObject.base
  } else {
    value = getCssVarColorValue(color)
  }
  return value
}

/**
 * Returns a CSS variable for the specified color token, or throws an error if the token
 * is invalid.
 */
function getCssVarColorValue(
  color: SemanticColorToken | NeutralColorToken | OverlayColorToken
): string {
  const keypath = color.split('.')

  let value: any
  if (keypath[0] in CssVars) {
    value = CssVars
  }

  for (const key of keypath) {
    if (key in value) {
      value = value[key]
    } else {
      throw new Error(`Invalid color token "${color}". Invalid key in keypath.`)
    }
  }

  if (typeof value === 'string') {
    return value
  }

  if (typeof value === 'object' && 'base' in value) {
    // NOTE(jare): This was an extended palette token, so we just return
    // the base color
    return value.base
  }

  throw new Error(`Invalid color token "${color}". Unable to find match.`)
}
